import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Button,
  Alert,
  AlertIcon,
  Box,
  Text,
  Spinner,
  useToast,
  Badge
} from '@chakra-ui/react';
import WhatsAppService from '../../../../../../services/whatsapp.service';
import whatsappConfig from '../../../../../../config/whatsapp.config';

/**
 * Modal component for creating a new WhatsApp group for a lead
 * 
 * @param {object} props Component props
 * @param {boolean} props.isOpen Whether the modal is open
 * @param {function} props.onClose Function to close the modal
 * @param {object} props.lead Lead data
 * @param {function} props.onSuccess Callback function when group is successfully created
 */
const WhatsAppGroupCreateModal = ({ isOpen, onClose, lead, onSuccess }) => {
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('input'); // 'input', 'creating', 'success'
  const toast = useToast();

  // Reset state when modal is opened & set default group name
  useEffect(() => {
    if (isOpen && lead) {
      const namePart = lead.brideAndGroomName || lead.name || '';
      const defaultGroupName = `${whatsappConfig.groupNameTemplate.prefix}${namePart}${namePart ? ' ' : ''}${whatsappConfig.groupNameTemplate.defaultSuffix}`;
      setGroupName(defaultGroupName);
      setError(null);
      setStep('input');
    }
  }, [isOpen, lead]);

  // Handle group name input change
  const handleGroupNameChange = (e) => {
    setGroupName(e.target.value);
    if (error) setError(null);
  };

  // Validate the entered group name
  const validateGroupName = () => {
    if (!groupName.trim()) {
      setError('Please enter a group name');
      return false;
    }
    return true;
  };

  // Create the WhatsApp group
  const createGroup = async () => {
    if (!validateGroupName()) return;
    if (!lead?.leadId) {
      setError('Lead ID is required');
      return;
    }

    setLoading(true);
    setStep('creating');
    setError(null);
    
    try {
      // Prepare lead data for group creation
      const leadData = {
        leadId: lead.leadId,
        brideAndGroomName: lead.brideAndGroomName || lead.name,
        phone: lead.phone
      };
      
      // Override the default group name if user changed it
      if (groupName !== `${whatsappConfig.groupNameTemplate.prefix}${lead.brideAndGroomName || lead.name || whatsappConfig.groupNameTemplate.defaultSuffix}`) {
        leadData.customGroupName = groupName;
      }
      
      console.log('[WhatsAppGroupCreateModal] Creating group with data:', leadData);
      
      const response = await WhatsAppService.createGroup(leadData);
      console.log('[WhatsAppGroupCreateModal] Group created:', response);
      
      setStep('success');
      
      toast({
        title: 'WhatsApp Group Created',
        description: `Successfully created the group "${response.groupName}" for the lead.`,
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      
      // Call the success callback with updated lead info
      if (onSuccess) {
        onSuccess({
          ...lead,
          whatsappGroup: response.groupId,
          whatsappGroupName: response.groupName
        });
      }
      
      // Automatically close after success
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      console.error('[WhatsAppGroupCreateModal] Error creating group:', err);
      setError(err.message || 'Failed to create WhatsApp group');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {step === 'input' && 'Create New WhatsApp Group'}
          {step === 'creating' && 'Creating WhatsApp Group...'}
          {step === 'success' && 'WhatsApp Group Created!'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          {error && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
          
          {/* Input Step */}
          {step === 'input' && (
            <FormControl>
              <FormLabel>WhatsApp Group Name</FormLabel>
              <Input
                placeholder="Enter group name"
                value={groupName}
                onChange={handleGroupNameChange}
              />
              <FormHelperText>
                This will be the name of the new WhatsApp group. The lead and team members will be automatically added to the group.
              </FormHelperText>
              
              {lead?.phone ? (
                <Box mt={4}>
                  <Text fontSize="sm" fontWeight="bold">Lead Phone:</Text>
                  <Badge colorScheme="green" mt={1}>{lead.phone}</Badge>
                  
                  <Text fontSize="sm" fontWeight="bold" mt={3}>Team Members to Add:</Text>
                  <Box mt={1}>
                    {whatsappConfig.teamMembers.map((member, idx) => (
                      <Badge key={idx} colorScheme="blue" mr={2} mb={2}>
                        {member.replace('@c.us', '')}
                      </Badge>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Alert status="warning" mt={4} borderRadius="md">
                  <AlertIcon />
                  Lead does not have a phone number. The group will be created without the lead as a member.
                </Alert>
              )}
            </FormControl>
          )}
          
          {/* Creating Step */}
          {step === 'creating' && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4} flexDirection="column">
              <Spinner size="lg" mb={4} />
              <Text>Creating new WhatsApp group...</Text>
              <Text fontSize="sm" mt={2} color="gray.500">This may take a few moments</Text>
            </Box>
          )}
          
          {/* Success Step */}
          {step === 'success' && (
            <Box textAlign="center" py={4}>
              <Text fontSize="lg" fontWeight="bold" color="green.500" mb={2}>
                Group Created Successfully!
              </Text>
              <Text>
                The WhatsApp group has been created and the lead record has been updated.
              </Text>
            </Box>
          )}
        </ModalBody>
        
        <ModalFooter>
          {step === 'input' && (
            <>
              <Button colorScheme="gray" mr={3} onClick={onClose}>Cancel</Button>
              <Button 
                colorScheme="green" 
                onClick={createGroup} 
                isLoading={loading}
                loadingText="Creating"
              >
                Create Group
              </Button>
            </>
          )}
          
          {step === 'creating' && (
            <Button colorScheme="gray" isDisabled>Creating...</Button>
          )}
          
          {step === 'success' && (
            <Button colorScheme="green" onClick={onClose}>
              Close
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WhatsAppGroupCreateModal;
